import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { solarPosition, solarVector, radians } from '../siteplan/solarPosition';

/**
 * Everything the site-plan and floor-plan solar viewers share: renderer,
 * camera + OrbitControls, the entrance reveal clock, hemisphere / sun lights
 * with shadow maps, the sun sprite, compass ring, seasonal arcs, ray guides,
 * dust motes, ground arrow, daylight grading, playback clock, the "special"
 * orbit, resize / context-loss handling and disposal.
 *
 * Content (towers, walls, ground plates) is added to `site` by the caller and
 * animated through `onTick`. World space is shared with `solarVector`: image
 * right = +X, image bottom = +Z, up = +Y; longest image side = `SITE_SIZE`.
 */
export interface SolarSceneState {
  month: number; minutes: number; north: number; latitude: number; longitude: number;
  playing: boolean; specialPlaying: boolean; rotating: boolean; speed: number; enabled: boolean; compare: boolean; reducedMotion: boolean;
}

export const SITE_SIZE = 120;

export interface SolarRigOptions {
  state: () => SolarSceneState;
  onTime: (minutes: number) => void;
  onPlaybackLoop: () => void;
  onError: (message: string) => void;
  /** Sun / compass radius in world units (site plan 88). */
  radius?: number;
  /** Resting camera offset from the target. */
  cameraOffset?: THREE.Vector3;
  minDistance?: number; maxDistance?: number;
  /** Ray-guide anchor points on the ground; defaults to the site-plan lanes. */
  beamAnchors?: Array<[number, number]>;
  /**
   * Whether the fixed studio light still casts shadows while the sun rig is off
   * (3D mode, or 3DS before north is calibrated). The site plan says no — 3D is
   * a plain look at the blocks and a shadow with no sun behind it is a lie. The
   * floor plan keeps it (default) for the contact shadow the dollhouse sits on.
   */
  unlitShadows?: boolean;
}

export interface TickContext {
  delta: number; timestamp: number; state: SolarSceneState;
  /** 0..1 reveal progress for the n-th revealed element (staggered). */
  reveal: (index: number) => number;
  entranceActive: boolean;
}

export function labelSprite(text: string, color = '#ffffff') {
  const canvas = document.createElement('canvas'); canvas.width = 192; canvas.height = 96;
  const ctx = canvas.getContext('2d')!;
  ctx.font = 'bold 48px Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.shadowColor = '#07101c'; ctx.shadowBlur = 8; ctx.fillStyle = color; ctx.fillText(text, 96, 48);
  const texture = new THREE.CanvasTexture(canvas);
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, depthTest: false, transparent: true, toneMapped: false }));
  sprite.scale.set(10, 5, 1); return sprite;
}

export function disposeTree(group: THREE.Object3D) {
  group.traverse(object => {
    const drawable = object as THREE.Mesh;
    drawable.geometry?.dispose();
    const materials = drawable.material ? (Array.isArray(drawable.material) ? drawable.material : [drawable.material]) : [];
    materials.forEach(material => {
      const map = (material as THREE.MeshBasicMaterial).map;
      if (map) map.dispose();
      material.dispose();
    });
  });
}

const ease = (value: number) => { const t = THREE.MathUtils.clamp(value, 0, 1); return t * t * (3 - 2 * t); };

export function createSolarRig(host: HTMLDivElement, options: SolarRigOptions) {
  const { state, onTime, onPlaybackLoop, onError } = options;
  const radius = options.radius ?? 88;
  // Keep the drawing buffer readable so print previews can copy the already-rendered scene without changing the live
  // scene, texture loading, or camera behavior.
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, host.clientWidth < 600 ? 1.5 : 2));
  renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 1.25;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  host.appendChild(renderer.domElement);
  const scene = new THREE.Scene(); scene.background = new THREE.Color('#101927');
  const camera = new THREE.PerspectiveCamera(43, 1, 1, 800);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true; controls.dampingFactor = 0.07; controls.autoRotateSpeed = 0.65;
  controls.minPolarAngle = 0.001; controls.maxPolarAngle = Math.PI / 2 - 0.05;
  controls.minDistance = options.minDistance ?? 65; controls.maxDistance = options.maxDistance ?? 460; controls.enablePan = false;
  // One scene-local clock survives texture/content rebuilds and filter changes.
  // Only a fresh viewer or Reset View replays the reveal.
  const tiltDuration = 0.85, riseDuration = 1.25, maxStagger = 0.65;
  const entranceDuration = tiltDuration + riseDuration + maxStagger;
  let entranceElapsed = 0, imageReady = false, entranceActive = !state().reducedMotion;
  let ownsEntranceCamera = true;
  const restingOffset = new THREE.Spherical().setFromVector3(options.cameraOffset ?? new THREE.Vector3(105, 122, 165));
  const placeEntranceCamera = (progress: number) => {
    const t = ease(progress);
    controls.target.set(0, 8 * t, 0);
    camera.position.setFromSpherical(new THREE.Spherical(restingOffset.radius,
      THREE.MathUtils.lerp(0.001, restingOffset.phi, t), restingOffset.theta * t)).add(controls.target);
  };
  const reset = () => {
    entranceElapsed = 0; entranceActive = !state().reducedMotion; ownsEntranceCamera = true;
    controls.autoRotate = false;
    // Flush residual orbit damping before restoring the starting pose.
    controls.enableDamping = false; controls.update(); controls.enableDamping = true;
    placeEntranceCamera(entranceActive ? 0 : 1); controls.update();
  };
  reset();
  const takeCameraControl = () => { ownsEntranceCamera = false; };
  controls.addEventListener('start', takeCameraControl);
  const ambient = new THREE.HemisphereLight('#bddbff', '#3f3442', 1.65); scene.add(ambient);
  const unlitShadows = options.unlitShadows ?? true;
  const sunLight = new THREE.DirectionalLight('#fff0d0', 3); sunLight.position.set(-80, 100, -60);
  sunLight.castShadow = true; sunLight.shadow.mapSize.set(2048, 2048);
  Object.assign(sunLight.shadow.camera, { left: -110, right: 110, top: 110, bottom: -110, near: 1, far: 500 });
  sunLight.shadow.bias = -0.0003; sunLight.shadow.normalBias = 0.12; sunLight.shadow.radius = 3;
  scene.add(sunLight, sunLight.target);
  const site = new THREE.Group(); scene.add(site);
  let disposed = false;
  const textures = new Set<THREE.Texture>();
  const reveal = (index: number) => entranceActive
    ? ease((entranceElapsed - tiltDuration - Math.min(index * 0.045, maxStagger)) / riseDuration) : 1;

  const solarGroup = new THREE.Group(); scene.add(solarGroup);
  const haloCanvas = document.createElement('canvas'); haloCanvas.width = haloCanvas.height = 128;
  const ctx = haloCanvas.getContext('2d')!;
  const gradient = ctx.createRadialGradient(64, 64, 1, 64, 64, 64);
  gradient.addColorStop(0, '#ffffff'); gradient.addColorStop(0.09, '#fff9ce');
  gradient.addColorStop(0.2, '#ffc75bcc'); gradient.addColorStop(0.48, '#ff9c3d44'); gradient.addColorStop(1, '#ff9c3d00');
  ctx.fillStyle = gradient; ctx.fillRect(0, 0, 128, 128);
  const haloTexture = new THREE.CanvasTexture(haloCanvas); textures.add(haloTexture);
  const sun = new THREE.Sprite(new THREE.SpriteMaterial({ map: haloTexture, transparent: true,
    blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false, color: '#ffc36d' })); sun.scale.set(30, 30, 1); solarGroup.add(sun);
  const compass = new THREE.Group(); solarGroup.add(compass);
  const ring = new THREE.LineLoop(new THREE.BufferGeometry().setFromPoints(Array.from({ length: 128 }, (_, i) =>
    new THREE.Vector3(Math.sin(i / 128 * Math.PI * 2) * radius, 0.5, Math.cos(i / 128 * Math.PI * 2) * radius))),
  new THREE.LineBasicMaterial({ color: '#748596', transparent: true, opacity: 0.35 })); compass.add(ring);
  ['N', 'E', 'S', 'W'].forEach((name, i) => {
    const label = labelSprite(name, name === 'N' ? '#ffbd65' : '#b7c8da');
    label.position.set(Math.sin(i * Math.PI / 2) * (radius + 6), 1, -Math.cos(i * Math.PI / 2) * (radius + 6)); compass.add(label);
  });
  const paths = new THREE.Group(); solarGroup.add(paths);
  let pathKey = '';
  const beamAnchors = options.beamAnchors ?? Array.from({ length: 7 }, (_, i) => [(i - 3) * 9, (i % 2 ? 1 : -1) * 18] as [number, number]);
  const beamGeometry = new THREE.BufferGeometry();
  const beamPositions = new Float32Array(6 * beamAnchors.length); beamGeometry.setAttribute('position', new THREE.BufferAttribute(beamPositions, 3));
  const beam = new THREE.LineSegments(beamGeometry, new THREE.LineBasicMaterial({ color: '#ffd494', transparent: true, opacity: 0.15 })); solarGroup.add(beam);
  const motesGeometry = new THREE.BufferGeometry();
  const moteCount = beamAnchors.length * 3;
  const motesPositions = new Float32Array(moteCount * 3); motesGeometry.setAttribute('position', new THREE.BufferAttribute(motesPositions, 3));
  const motes = new THREE.Points(motesGeometry, new THREE.PointsMaterial({ map: haloTexture, color: '#ffd18e', size: 2.2,
    transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false })); solarGroup.add(motes);
  const projection = new THREE.ArrowHelper(new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 0.7, 0), 25, '#ffc778', 3, 1.5); solarGroup.add(projection);
  const contextLost = (event: Event) => { event.preventDefault(); onError('3D graphics were interrupted. Return to 2D and reopen 3D to retry.'); };
  renderer.domElement.addEventListener('webglcontextlost', contextLost);
  const resize = new ResizeObserver(() => {
    const w = host.clientWidth; const h = host.clientHeight;
    if (!w || !h) return; renderer.setSize(w, h); camera.aspect = w / h;
    camera.zoom = Math.min(1, camera.aspect / 1.2); camera.updateProjectionMatrix();
  }); resize.observe(host);
  const ticks: Array<(context: TickContext) => void> = [];
  const disposables: Array<() => void> = [];
  let raf = 0, previous = 0, lastUI = 0, simulated = state().minutes, lastRequested = simulated;
  let specialWasPlaying = false, specialCycleStartMinutes = 390;
  let specialStartTheta = 0, specialStartPhi = restingOffset.phi, specialStartRadius = restingOffset.radius;
  const specialOffset = new THREE.Vector3();
  const specialSpherical = new THREE.Spherical();
  const direction = new THREE.Vector3();
  const animate = (timestamp: number) => {
    if (disposed) return; raf = requestAnimationFrame(animate);
    const delta = previous ? Math.min(0.05, (timestamp - previous) / 1000) : 0; previous = timestamp;
    if (document.hidden) return;
    const s = state();
    if (entranceActive) {
      // Wait for the image: a slow download must not consume the entrance.
      if (imageReady) entranceElapsed += delta;
      if (s.reducedMotion) entranceElapsed = entranceDuration;
      if (ownsEntranceCamera) placeEntranceCamera(entranceElapsed / tiltDuration);
      if (entranceElapsed >= entranceDuration) entranceActive = false;
    }
    if (s.minutes !== lastRequested) { simulated = s.minutes; lastRequested = s.minutes; }
    if ((s.playing || s.specialPlaying) && s.enabled) {
      simulated += delta * 30 * s.speed;
      if (simulated > 1170) {
        simulated = 390 + (simulated - 1170);
        if (s.specialPlaying) { specialCycleStartMinutes = 390; onPlaybackLoop(); }
      }
    }
    const pos = solarPosition(s.month, simulated, s.latitude, s.longitude);
    const vector = solarVector(pos.azimuth, pos.elevation, s.north);
    // Interpolate Cartesian direction, never azimuth: avoids a discontinuity at zenith/north.
    direction.lerp(new THREE.Vector3(vector.x, vector.y, vector.z), 1 - Math.exp(-delta * 10));
    if (direction.lengthSq() < 0.001) direction.set(vector.x, vector.y, vector.z);
    direction.normalize();
    solarGroup.visible = s.enabled && (!entranceActive || entranceElapsed >= tiltDuration);
    compass.rotation.y = -s.north * radians;
    if (s.specialPlaying && !s.reducedMotion && !entranceActive) {
      if (!specialWasPlaying) {
        ownsEntranceCamera = false;
        specialSpherical.setFromVector3(specialOffset.copy(camera.position).sub(controls.target));
        specialStartTheta = specialSpherical.theta;
        specialStartPhi = THREE.MathUtils.clamp(specialSpherical.phi, 0.38, 1.15);
        specialStartRadius = THREE.MathUtils.clamp(specialSpherical.radius, controls.minDistance, controls.maxDistance);
        specialCycleStartMinutes = simulated;
      }
      const progress = THREE.MathUtils.clamp((simulated - specialCycleStartMinutes) / (1170 - specialCycleStartMinutes), 0, 1);
      const zoomPulse = Math.pow(Math.max(0, Math.sin(progress * Math.PI * 6)), 2);
      specialSpherical.set(
        THREE.MathUtils.clamp(specialStartRadius * (1 + Math.sin(progress * Math.PI * 2) * 0.08 - zoomPulse * 0.16), controls.minDistance, controls.maxDistance),
        THREE.MathUtils.clamp(specialStartPhi + Math.sin(progress * Math.PI * 4) * 0.2, controls.minPolarAngle, controls.maxPolarAngle),
        specialStartTheta + progress * Math.PI * 2,
      );
      camera.position.setFromSpherical(specialSpherical).add(controls.target);
    }
    specialWasPlaying = s.specialPlaying && !s.reducedMotion && !entranceActive;
    controls.autoRotate = !specialWasPlaying && s.rotating && !s.reducedMotion && !entranceActive; controls.update(delta);
    if (s.enabled) {
      sun.position.copy(direction).multiplyScalar(radius);
      sun.visible = pos.elevation > -2;
      sunLight.position.copy(direction).multiplyScalar(180);
      const daylight = THREE.MathUtils.smoothstep(pos.elevation, -1, 15);
      sunLight.intensity = 3.2 * daylight;
      sunLight.color.setHSL(0.075 + Math.max(0, direction.y) * 0.045, 0.65 - Math.max(0, direction.y) * 0.4, 0.82);
      ambient.intensity = 0.6 + daylight * 0.85;
      (scene.background as THREE.Color).set('#101927').lerp(new THREE.Color('#302535'), (1 - daylight) * 0.5);
      const horizontal = new THREE.Vector3(-direction.x, 0, -direction.z);
      projection.visible = pos.elevation > 0 && horizontal.length() > 0.03;
      projection.setDirection(horizontal.normalize()); projection.setLength(28, 3, 1.5);
      beamAnchors.forEach(([x, z], i) => {
        const end = new THREE.Vector3(x, 0.9, z);
        const start = end.clone().addScaledVector(direction, 65);
        beamPositions.set([start.x, start.y, start.z, end.x, end.y, end.z], i * 6);
      });
      beamGeometry.attributes.position.needsUpdate = true; beamGeometry.computeBoundingSphere(); beam.visible = pos.elevation > 0;
      motes.visible = beam.visible && !s.reducedMotion;
      for (let i = 0; i < moteCount; i++) {
        const [x, z] = beamAnchors[i % beamAnchors.length];
        const travel = 1 - ((timestamp / 6000 + Math.floor(i / beamAnchors.length) / 3) % 1);
        motesPositions.set([x + direction.x * travel * 65, 0.9 + direction.y * travel * 65, z + direction.z * travel * 65], i * 3);
      }
      motesGeometry.attributes.position.needsUpdate = true; motesGeometry.computeBoundingSphere();
      const key = `${s.month}:${s.north}:${s.latitude}:${s.longitude}:${s.compare}`;
      if (key !== pathKey) {
        pathKey = key; disposeTree(paths); paths.clear();
        (s.compare ? [5, 11, s.month] : [s.month]).forEach((month, index, months) => {
          const active = index === months.length - 1;
          const points: THREE.Vector3[] = [];
          for (let m = 360; m <= 1200; m += 4) {
            const p = solarPosition(month, m, s.latitude, s.longitude);
            if (p.elevation < 0) continue;
            const v = solarVector(p.azimuth, p.elevation, s.north); points.push(new THREE.Vector3(v.x, v.y, v.z).multiplyScalar(radius));
          }
          paths.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(points),
            new THREE.LineBasicMaterial({ color: active ? '#ffc66b' : month === 5 ? '#73cfff' : '#b99aff', transparent: true, opacity: active ? 0.85 : 0.3 })));
        });
      }
    } else { sunLight.position.set(-80, 100, -60); sunLight.intensity = 2.5; sunLight.color.set('#fff0d0'); ambient.intensity = 1.65; }
    // Toggling `castShadow` recompiles every material, so only write on a change.
    const wantsShadows = s.enabled || unlitShadows;
    if (sunLight.castShadow !== wantsShadows) sunLight.castShadow = wantsShadows;
    const context: TickContext = { delta, timestamp, state: s, reveal, entranceActive };
    ticks.forEach(tick => tick(context));
    renderer.render(scene, camera);
    if ((s.playing || s.specialPlaying) && timestamp - lastUI > 100) { lastUI = timestamp; lastRequested = simulated; onTime(simulated); }
  };
  raf = requestAnimationFrame(animate);
  return {
    renderer, scene, camera, controls, site, textures, reveal,
    get disposed() { return disposed; },
    /** Register per-frame content animation (runs after lighting, before render). */
    onTick: (tick: (context: TickContext) => void) => { ticks.push(tick); },
    onDispose: (fn: () => void) => { disposables.push(fn); },
    /** The entrance clock waits for the primary image; call once it is on the GPU. */
    markImageReady: () => { imageReady = true; },
    reset,
    getView: () => ({ position: camera.position.toArray() as [number, number, number],
      target: controls.target.toArray() as [number, number, number] }),
    setView: (view: { position: [number, number, number]; target: [number, number, number] }) => {
      entranceActive = false; ownsEntranceCamera = false; controls.autoRotate = false;
      camera.position.fromArray(view.position); controls.target.fromArray(view.target); controls.update();
    },
    zoom: (factor: number) => { takeCameraControl(); camera.position.sub(controls.target).multiplyScalar(factor).add(controls.target); controls.update(); },
    dispose: () => {
      disposed = true; cancelAnimationFrame(raf); resize.disconnect(); controls.removeEventListener('start', takeCameraControl); controls.dispose();
      renderer.domElement.removeEventListener('webglcontextlost', contextLost);
      disposables.forEach(fn => fn());
      disposeTree(scene); textures.forEach(t => t.dispose()); sunLight.shadow.map?.dispose(); renderer.dispose(); renderer.domElement.remove();
    },
  };
}

export type SolarRig = ReturnType<typeof createSolarRig>;

export interface GroundCrop { x: number; y: number; w: number; h: number }

/**
 * A textured ground plate + plinth sized to the image aspect (longest side =
 * SITE_SIZE). `crop` is a percentage rectangle of the image; the plate shows
 * only that region. `onReady` fires with the plate width / depth once the
 * texture is on the GPU.
 */
export function createGroundPlate(rig: SolarRig, imageUrl: string, options: {
  crop?: GroundCrop | null; mirror?: boolean;
  /**
   * Tried once if `imageUrl` fails to load. WebGL needs a CORS-clean image, so
   * this is how the site plan falls back between the image's own host and our
   * same-origin proxy without either one having to be known good.
   */
  fallbackUrl?: string | null;
  onReady?: (width: number, depth: number, image: HTMLImageElement) => void; onError: (message: string) => void;
}) {
  let depth = 100; let width = SITE_SIZE;
  const groundMaterial = new THREE.MeshStandardMaterial({ color: '#69798b', roughness: 0.94 });
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), groundMaterial);
  ground.rotation.x = -Math.PI / 2; ground.receiveShadow = true; rig.site.add(ground);
  const plinth = new THREE.Mesh(new THREE.BoxGeometry(width, 1.2, depth),
    new THREE.MeshStandardMaterial({ color: '#283a4e', roughness: 0.7, metalness: 0.35 }));
  plinth.position.y = -0.9; rig.site.add(plinth);
  const crop = options.crop && options.crop.w > 0 && options.crop.h > 0 ? options.crop : null;
  const loader = new THREE.TextureLoader(); loader.setCrossOrigin('anonymous');
  const applyTexture = (loaded: THREE.Texture) => {
    if (rig.disposed) { loaded.dispose(); return; }
    loaded.colorSpace = THREE.SRGBColorSpace; loaded.anisotropy = Math.min(8, rig.renderer.capabilities.getMaxAnisotropy());
    const img = loaded.image as HTMLImageElement;
    const shownWidth = img.width * (crop ? crop.w / 100 : 1), shownHeight = img.height * (crop ? crop.h / 100 : 1);
    const longestSide = Math.max(shownWidth, shownHeight);
    width = SITE_SIZE * shownWidth / longestSide; depth = SITE_SIZE * shownHeight / longestSide;
    const region = crop ?? { x: 0, y: 0, w: 100, h: 100 };
    loaded.wrapS = loaded.wrapT = THREE.ClampToEdgeWrapping;
    // Texture V runs bottom-up, so the offset is measured from the image bottom.
    // A negative U repeat flips the plate horizontally without touching mesh
    // winding, so shadows and lighting stay correct for mirrored stacks.
    loaded.repeat.set((options.mirror ? -1 : 1) * region.w / 100, region.h / 100);
    loaded.offset.set((options.mirror ? region.x + region.w : region.x) / 100, 1 - (region.y + region.h) / 100);
    ground.geometry.dispose(); ground.geometry = new THREE.PlaneGeometry(width, depth);
    plinth.geometry.dispose(); plinth.geometry = new THREE.BoxGeometry(width, 1.2, depth);
    groundMaterial.color.set('#ffffff'); groundMaterial.map = loaded; groundMaterial.needsUpdate = true;
    rig.markImageReady();
    options.onReady?.(width, depth, img);
  };
  const fail = () => {
    if (!rig.disposed) options.onError('The image could not be loaded into 3D. Check image access / CORS or return to 2D.');
  };
  const retryUrl = options.fallbackUrl && options.fallbackUrl !== imageUrl ? options.fallbackUrl : null;
  const texture = loader.load(imageUrl, applyTexture, undefined, () => {
    if (rig.disposed || !retryUrl) { fail(); return; }
    rig.textures.add(loader.load(retryUrl, applyTexture, undefined, fail));
  });
  rig.textures.add(texture);
  return { ground, plinth, get width() { return width; }, get depth() { return depth; } };
}
